import { Component, OnDestroy, OnInit } from "@angular/core";
import { AuthService } from "../../auth/auth.service";
import { DataService } from "../../data.service";
import { GtagService } from "../../gtag/gtag.service";
import { TracknumQueueItem, CargoFeaturesModel } from "../../model";
import { DateUtils } from "../../shared/date-utils";
import { SerialPort } from "../../shared/domSerial";
import { PackageState } from "../../model/TracknumQueueItem";

@Component({
    selector: 'app-admin-package-received',
    templateUrl: './admin-package-received.component.html',
    styleUrls: ['./admin-package-received.component.css'],
    standalone: false
})
export class AdminPackageReceivedComponent implements OnInit, OnDestroy {
    scanValue: string = "";

    weightValue?: number = undefined;
    tracknumbers: TracknumQueueItem[] = [];
    connected: boolean = false;

    cargoFeatures?: CargoFeaturesModel;
    showDialog: boolean = false;
    packageDate: any = new Date();
    tracksData: string = '';
    currentRowNumber: number = 1;

    processingQueueIntervalId: any; // need for clearInterval call
    resendingQueueIntervalId: any; // need for clearInterval call

    public get packageState(): typeof PackageState {
        return PackageState;
    }

    constructor(private dataService: DataService, private authService: AuthService, private gtag: GtagService) {
        this.gtag.pageview2('Track received');

        authService.cargoFeatures().then((data: CargoFeaturesModel) => {
            this.cargoFeatures = data;
        });
    }

    ngOnInit() {
        var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

        // serial not working with safari
        if (!isSafari) {
            if (!(navigator as any).serial) {
                console.error('Serial API is not supported');
            } else {
                (navigator as any).serial.onconnect = (event: any) => {
                    console.log(event);
                    this.readWeightData(event.srcElement);
                }
            }
        }

        // run tracks processing queue
        this.runTracksProcessingQueue(2000);
        // run resend processing queue
        this.runTracksResendQueue(30000);
    }

    ngOnDestroy(): void {
        if (this.processingQueueIntervalId) {
            clearInterval(this.processingQueueIntervalId);
        }

        if (this.resendingQueueIntervalId) {
            clearInterval(this.resendingQueueIntervalId);
        }
    }

    /**
    * Processig tracks with status WAITING and UNKNOWN
    * 
    * @param processingInterval 
    */
    runTracksProcessingQueue(processingInterval: number) {
        this.processingQueueIntervalId = setInterval(() => {
            this.tracknumbers.forEach((num) => {
                if (num.state === PackageState.waiting || num.state === PackageState.unknown) {

                    num.state = PackageState.processing;
                    // send data to the server
                    const subscr = this.dataService.trackReceived({
                        tracknum: num.tracknum,
                        create_track: true,
                        package_weight: this.weightValue,
                        package_date: num.packageDate ? DateUtils.unixTimestamp(num.packageDate) : undefined,
                    }).subscribe({
                        next: (data) => {
                            num.state = PackageState.processed;
                            num.userInfo = { ...data?.client };
                            this.gtag.event('track_received', { enevnt_category: 'success', track_number: num.tracknum });
                        },
                        error: (err) => {
                            subscr.unsubscribe();
                            if (err.message === 'already_received') {
                                num.state = PackageState.already_processed;
                            }
                            this.gtag.event('track_received', { enevnt_category: `error: ${num.state}`, track_number: num.tracknum });
                        },
                        complete: () => {
                            subscr.unsubscribe();
                        }
                    });
                }
            });
        }, processingInterval);
    }


    /**
     * Change state of tracks with status PROCESSING to status WAITING. 
     * This needed to resend the tracks for processing again (reporcessing) 
     * 
     * @param processingInterval 
     */
    runTracksResendQueue(processingInterval: number) {
        this.resendingQueueIntervalId = setInterval(() => {
            this.tracknumbers.forEach((num) => {
                if (num.state === PackageState.processing) {
                    num.state = PackageState.waiting;
                }
            });
        }, processingInterval);
    }

    readBarcode(event: KeyboardEvent) {
        if (this.cargoFeatures?.isManualInput) {
            return;
        };

        let char = event.code.replace(/Key|Digit|Numpad(.*)/g, '$1');
        switch (char) {
            case 'Minus':
                this.scanValue += '-';
                break;
            case 'Enter':
                this.tracknumbers.unshift({
                    rownum: this.currentRowNumber++,
                    tracknum: this.scanValue,
                    time: new Date(),
                    state: PackageState.waiting,
                    packageDate: null
                });

                this.scanValue = '';
                break;
            default:
                this.scanValue += char;
        }


    }

    onKeyUp(event: KeyboardEvent) {
        if (event.keyCode === 13) {
            this.saveScannedValue();
        }
    }

    saveScannedValue() {
        if (this.scanValue.length === 0) {
            alert("Введите или отсканируйте трек-код");
            return;
        }

        this.tracknumbers.unshift({
            rownum: this.currentRowNumber++,
            tracknum: this.scanValue,
            time: new Date(),
            state: PackageState.waiting,
            packageDate: null
        });

        this.scanValue = '';
    }

    clearValue() {
        this.scanValue = "";
    }

    async readWeightData(port: SerialPort) {
        try {
            await port.open({ baudRate: 9600 });
            port.ondisconnect = (event: any) => {
                this.connected = false;
                console.log(event);
            }

            this.connected = true;

            let reader = port.readable.getReader();
            let buff: any[] = [];
            // Listen to data coming from the serial device.
            while (true) {
                const { value, done } = await reader.read();
                if (done) {
                    // Allow the serial port to be closed later.
                    reader.releaseLock();
                    console.log("Release port");
                    break;
                }

                // value is a Uint8Array.
                for (let i = 0; i < value.length; i++) {
                    if (value[i] !== 13) {
                        buff.push(value[i]);
                    } else {
                        this.weightValue = parseFloat(String.fromCharCode(...buff));
                        buff = [];
                    }
                }

                console.log(value); // for debug purposes only
            }
        } catch (e) {
            console.error('Error connecting to the serial port:', e);
        }
    }

    async connectToScale() {
        try {
            let port = await (navigator as any).serial.requestPort();
            this.readWeightData(port);
        } catch (error) {
            console.error('Error connecting to the serial port:', error)
        }
    }

    /**
     * Split a list of tracking numbers into separate tracks and add them into queue
     */
    bulkImport() {
        // split inserted text into rows
        const tracksList = this.tracksData.split("\n")

        tracksList.forEach(track => {
            // remove all double quotes and spaces
            const tracknum = track.replace(/\"/g, '').trim();
            // return if no number
            if (tracknum === "") {
                return;
            }

            this.tracknumbers.unshift({
                rownum: this.currentRowNumber++,
                tracknum: tracknum,
                time: this.packageDate,
                state: PackageState.waiting,
                packageDate: this.packageDate
            });
        });

        this.cancelImport();
    }

    showImportPackagesDlg() {
        this.showDialog = true;
    }

    cancelImport() {
        this.tracksData = '';
        this.showDialog = false;
    }


}
